import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface Integration {
  apiKey: string
  integrationId: number | null
  status: 'pending' | 'authenticated' | 'error'
  connectedAt?: string
  /** Coarse auth state coming from the login-resume flow. */
  authStatus?: 'ready' | 'auth_required' | 'error'
}

export interface VerificationState {
  status: 'idle' | 'loading' | 'success' | 'error'
  error?: string
  account?: Record<string, unknown>
}

/**
 * User context carried through the store-login flow. Mirrors the
 * backend {@code StoreLoginUserDTO}.
 */
export interface AuthenticatedUser {
  id: number
  email: string
  group: string
}

/**
 * A single store the user is allowed to operate against.
 */
export interface AvailableStore {
  storeId: string
  storeName: string
  storeDb: string
  group: string
}

/**
 * A provider option surfaced during the multi-provider selection step.
 */
export interface ProviderOption {
  provider: string
  displayName: string
  loginType: string
  integrationId: number | null
}

export const useAuthStore = defineStore('auth', () => {
  const invoiceNumber = ref('')
  const storeID = ref('')
  const invoiceHasAddon = ref(false)
  const selectedPackageId = ref<string | undefined>(undefined)
  const verification = ref<VerificationState>({ status: 'idle' })
  const credentials = ref<Record<string, string>>({})
  const integrations = ref<Record<string, Integration>>({})

  // --- Store-login state (login → store selection → dashboard) ---
  const user = ref<AuthenticatedUser | null>(null)
  const availableStores = ref<AvailableStore[]>([])
  const selectedStore = ref<AvailableStore | null>(null)
  const providerOptions = ref<ProviderOption[]>([])
  const pendingAuthRedirect = ref<string | null>(null)

  const isAuthenticated = computed(() =>
    Object.values(integrations.value).some(i => i.status === 'authenticated'),
  )

  const hasUserContext = computed(() => user.value !== null)
  const hasSelectedStore = computed(() => selectedStore.value !== null)

  const currentApiKey = computed(() => {
    const prov = selectedPackageId.value?.toUpperCase()
    if (prov && integrations.value[prov]?.status === 'authenticated') {
      return integrations.value[prov]?.apiKey
    }
    // Fallback: first authenticated integration
    const first = Object.values(integrations.value).find(i => i.status === 'authenticated')
    return first?.apiKey
  })

  const currentIntegration = computed(() => {
    const prov = selectedPackageId.value?.toUpperCase()
    if (prov && integrations.value[prov]?.status === 'authenticated') {
      return integrations.value[prov]
    }
    // Fallback: first authenticated integration
    return Object.values(integrations.value).find(i => i.status === 'authenticated')
  })

  function setQueryParams(params: { invoiceNumber?: string; storeID?: string }) {
    invoiceNumber.value = params.invoiceNumber ?? ''
    storeID.value = params.storeID ?? ''
  }

  function setInvoiceHasAddon(val: boolean) {
    invoiceHasAddon.value = !!val
  }

  function selectPackage(id: string) {
    selectedPackageId.value = id
  }

  function setVerificationStatus(status: VerificationState['status'], payload: Partial<VerificationState> = {}) {
    verification.value = { status, error: payload.error, account: payload.account }
  }

  function setCredentials(creds: Record<string, string>) {
    credentials.value = creds
  }

  function clearCredentials() {
    credentials.value = {}
  }

  function setIntegrationResult(info: {
    provider: string
    apiKey: string
    integrationId?: number
    status?: Integration['status']
    connectedAt?: string
    authStatus?: Integration['authStatus']
  }) {
    const key = info.provider.toUpperCase()
    integrations.value[key] = {
      apiKey: info.apiKey,
      integrationId: info.integrationId ?? null,
      status: info.status ?? 'authenticated',
      connectedAt: info.connectedAt ?? new Date().toISOString(),
      authStatus: info.authStatus,
    }
    // Auto-select this integration if nothing is currently selected
    if (!selectedPackageId.value) {
      selectedPackageId.value = key
    }
  }

  function getIntegration(provider: string): Integration | undefined {
    return integrations.value[provider.toUpperCase()]
  }

  function clearIntegration(provider?: string) {
    if (!provider) {
      integrations.value = {}
      selectedPackageId.value = undefined
      return
    }
    const key = provider.toUpperCase()
    delete integrations.value[key]
    if (selectedPackageId.value?.toUpperCase() === key) {
      selectedPackageId.value = undefined
    }
  }

  function clearSelectedPackage() {
    selectedPackageId.value = undefined
  }

  // --- Store-login actions ---

  function setUserContext(authenticatedUser: AuthenticatedUser, stores: AvailableStore[]) {
    user.value = authenticatedUser
    availableStores.value = stores ?? []
  }

  function selectStore(store: AvailableStore | null) {
    selectedStore.value = store
  }

  function setProviderOptions(options: ProviderOption[]) {
    providerOptions.value = options ?? []
  }

  function setProviderAuthRequired(provider: string | null, redirectUrl?: string | null) {
    if (provider) {
      // Mark the integration as pending so the workspace does not treat
      // it as authenticated while the user re-authorizes.
      const key = provider.toUpperCase()
      const existing = integrations.value[key]
      if (existing) {
        existing.authStatus = 'auth_required'
        existing.status = 'pending'
      }
    }
    pendingAuthRedirect.value = redirectUrl ?? null
  }

  /**
   * Removes all login-resume state. Does NOT clear persisted
   * provider integrations (use {@link clearIntegration} for that).
   */
  function clearUserContext() {
    user.value = null
    availableStores.value = []
    selectedStore.value = null
    providerOptions.value = []
    pendingAuthRedirect.value = null
  }

  /**
   * Full logout. Clears user context, persisted login params, and any
   * existing provider integrations.
   */
  function logout() {
    clearUserContext()
    invoiceNumber.value = ''
    storeID.value = ''
    invoiceHasAddon.value = false
    credentials.value = {}
    integrations.value = {}
    selectedPackageId.value = undefined
  }

  return {
    invoiceNumber,
    storeID,
    invoiceHasAddon,
    selectedPackageId,
    verification,
    credentials,
    integrations,
    user,
    availableStores,
    selectedStore,
    providerOptions,
    pendingAuthRedirect,
    isAuthenticated,
    hasUserContext,
    hasSelectedStore,
    currentApiKey,
    currentIntegration,
    setQueryParams,
    setInvoiceHasAddon,
    selectPackage,
    setVerificationStatus,
    setCredentials,
    clearCredentials,
    setIntegrationResult,
    getIntegration,
    clearIntegration,
    clearSelectedPackage,
    setUserContext,
    selectStore,
    setProviderOptions,
    setProviderAuthRequired,
    clearUserContext,
    logout,
  }
}, {
  persist: {
    key: 'auth',
    storage: localStorage,
    pick: [
      'invoiceNumber',
      'storeID',
      'invoiceHasAddon',
      'integrations',
      'selectedPackageId',
      'user',
      'selectedStore',
      // availableStores and providerOptions are intentionally NOT persisted:
      // they are short-lived during the login flow and should be re-fetched.
    ],
  },
})
